import { BATCH6_PRECON_BLOG_SEO } from './seoConfig.blogs';
/**
 * Trilingual SEO metadata registry.
 *
 * Every public page has an entry with title + description in EN, ES, and FR.
 * Private/auth/admin pages only need a title (robots=noindex so description
 * is never shown in search results).
 *
 * Usage in a page component:
 *
 *   import { usePageSEO } from '../config/seoConfig';
 *   function LandingPage() {
 *     usePageSEO('home');          // reads active language automatically
 *     ...
 *   }
 *
 * Or if you need the raw object (e.g. to override ogImage):
 *
 *   import { getPageSEO } from '../config/seoConfig';
 *   const seo = getPageSEO('home', 'fr');
 */

import { useSEO, SEOProps } from '../hooks/useSEO';
import { useLanguage, Language } from '../contexts/LanguageContext';

const BASE_URL = 'https://hipotech.net';

// ── Types ──────────────────────────────────────────────────────────────────

interface LocalizedMeta {
  title: string;
  description?: string;
}

interface PageSEOEntry {
  en: LocalizedMeta;
  es: LocalizedMeta;
  fr: LocalizedMeta;
  robots?: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  /** Optional Schema.org JSON-LD object passed through to useSEO */
  schemaOrg?: Record<string, unknown>;
}

// ── Registry ───────────────────────────────────────────────────────────────

const SEO_REGISTRY: Record<string, PageSEOEntry> = {

  // ── Home ──────────────────────────────────────────────────────────────────
  home: {
    en: {
      title: 'HipoTech – Dominican Republic (DR) Mortgages for Foreign Buyers',
      description: 'Submit one application to multiple DR banks simultaneously. The multi-bank mortgage platform for buyers in the Dominican Republic — compare offers, track status, get approved in days.',
    },
    es: {
      title: 'HipoTech – Hipoteca Multi-Banco en República Dominicana (RD) para Extranjeros',
      description: 'Envía una solicitud a múltiples bancos en RD simultáneamente. La plataforma de hipoteca multi-banco para compradores extranjeros en República Dominicana — compara ofertas y recibe aprobación en días.',
    },
    fr: {
      title: 'HipoTech – Prêt Hypothécaire Multi-Banques en République Dominicaine (RD) pour Étrangers',
      description: 'Soumettez une demande à plusieurs banques en RD simultanément. La plateforme de prêt hypothécaire multi-banques pour acheteurs étrangers en République Dominicaine — comparez les offres, obtenez une approbation en quelques jours.',
    },
    robots: 'index,follow',
    canonical: `/`,
    ogType: 'website',
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'FinancialService',
      name: 'HipoTech',
      url: BASE_URL,
      description:
        'Multi-bank mortgage platform for buyers in the Dominican Republic (DR). Submit one application — every qualifying bank receives it simultaneously.',
      areaServed: { '@type': 'Country', name: 'Dominican Republic' },
      serviceType: 'Mortgage Application',
      availableLanguage: ['English', 'Spanish', 'French'],
      sameAs: [`${BASE_URL}/about`],
    },
  },

  // ── Learn More ────────────────────────────────────────────────────────────
  learnMore: {
    en: {
      title: 'How HipoTech Works — DR Multi-Bank Mortgage for Foreign Buyers',
      description: 'Four simple steps: profile, documents, secure payment, then every qualifying DR bank receives your application simultaneously. Pre-approval in days, not months.',
    },
    es: {
      title: 'Cómo Funciona HipoTech',
      description: 'Sigue cuatro pasos simples: elige tu producto, completa tu perfil, sube tus documentos y recibe ofertas de múltiples bancos. Así de fácil.',
    },
    fr: {
      title: 'Comment Fonctionne HipoTech',
      description: 'Quatre étapes simples : choisissez votre produit, complétez votre profil, téléchargez vos documents et recevez des offres de plusieurs banques.',
    },
    robots: 'index,follow',
    canonical: `/learn-more`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to Apply for a Mortgage in the Dominican Republic with HipoTech',
      description: 'Apply for a DR mortgage and receive offers from multiple banks simultaneously in four steps.',
      totalTime: 'PT30M',
      step: [
        {
          '@type': 'HowToStep',
          position: 1,
          name: 'Choose Your Product',
          text: 'Select between a Personal Pre-Approval (based on your financial profile) or a Property Pre-Approval (tied to a specific property).',
        },
        {
          '@type': 'HowToStep',
          position: 2,
          name: 'Complete Your Profile',
          text: 'Fill in your personal and financial information. HipoTech uses zero-persistence security — sensitive data is never stored on our servers.',
        },
        {
          '@type': 'HowToStep',
          position: 3,
          name: 'Upload Your Documents',
          text: 'Upload the required documents securely. All files are encrypted with AES-256 and protected by split-key encryption.',
        },
        {
          '@type': 'HowToStep',
          position: 4,
          name: 'Receive Bank Offers',
          text: 'Your application is distributed simultaneously to every qualifying Dominican Republic bank. Compare offers, track status in real time, and choose the best terms.',
        },
      ],
    },
  },

  // ── FAQ ───────────────────────────────────────────────────────────────────
  faq: {
    en: {
      title: 'FAQ — DR Mortgage for Foreign Buyers | HipoTech',
      description: 'Answers to the most common questions about mortgages in the Dominican Republic: eligibility for foreign buyers, pre-approval, interest rates, required documents, multi-bank process and more.',
    },
    es: {
      title: 'Preguntas Frecuentes sobre Hipotecas en RD',
      description: 'Respuestas a las dudas más comunes sobre hipotecas en República Dominicana: requisitos, tasas, plazos, documentos y el proceso con HipoTech.',
    },
    fr: {
      title: 'Questions Fréquentes sur les Prêts Hypothécaires en RD',
      description: 'Réponses aux questions les plus courantes sur les prêts hypothécaires en République Dominicaine : exigences, taux, délais, documents et le processus HipoTech.',
    },
    robots: 'index,follow',
    canonical: `/faq`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What makes HipoTech different from going to banks directly?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'HipoTech submits your mortgage application to multiple Dominican Republic banks simultaneously with a single application. Instead of spending weeks visiting banks one by one, you receive competing offers from every qualifying bank at once, letting you compare rates and terms before committing.',
          },
        },
        {
          '@type': 'Question',
          name: 'Who can apply for a mortgage through HipoTech?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Foreign buyers and non-residents can apply for a mortgage in the Dominican Republic through HipoTech. You do not need to be a Dominican resident. HipoTech specializes in helping international buyers navigate the DR mortgage process.',
          },
        },
        {
          '@type': 'Question',
          name: 'Do I need to be a resident of the Dominican Republic?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. HipoTech is designed specifically for foreign buyers and non-residents. You can apply for a Dominican Republic mortgage from anywhere in the world.',
          },
        },
        {
          '@type': 'Question',
          name: 'What products does HipoTech offer?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'HipoTech offers two mortgage pre-approval products: Personal Pre-Approval, which assesses your creditworthiness as a borrower before you identify a property, and Property Pre-Approval, which ties the application to a specific property you intend to purchase.',
          },
        },
        {
          '@type': 'Question',
          name: 'Does HipoTech store my sensitive financial information?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No. HipoTech uses zero-persistence security for sensitive financial data like bank account and routing numbers. This data is used only during the active application session and is cryptographically destroyed immediately after transmission to the banks. It is never stored on HipoTech servers.',
          },
        },
        {
          '@type': 'Question',
          name: 'What encryption does HipoTech use?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'HipoTech uses AES-256 encryption for documents at rest and TLS 1.3 for all data in transit. Documents are protected with split-key encryption, meaning no single party holds the complete decryption key.',
          },
        },
        {
          '@type': 'Question',
          name: 'What credit score do I need?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Credit requirements vary by bank. HipoTech\'s AI-powered bank matching system evaluates your profile and submits your application only to banks whose criteria you are likely to meet, maximizing your chances of approval.',
          },
        },
        {
          '@type': 'Question',
          name: 'What languages are supported?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'HipoTech is fully available in English, Spanish, and French, reflecting the international profile of buyers in the Dominican Republic real estate market.',
          },
        },
      ],
    },
  },

  // ── Features ──────────────────────────────────────────────────────────────
  features: {
    en: {
      title: 'Platform Features | HipoTech DR Mortgage',
      description: 'Multi-bank distribution, AI-powered bank matching, real-time tracking, zero-persistence security and integrated co-applicant support. Everything a foreign buyer needs for a DR mortgage.',
    },
    es: {
      title: 'Funcionalidades de la Plataforma | HipoTech',
      description: 'Gestión de documentos, comparación de bancos, seguimiento en tiempo real y co-solicitante integrado. Todo lo que necesitas para tu hipoteca en un solo lugar.',
    },
    fr: {
      title: 'Fonctionnalités de la Plateforme | HipoTech',
      description: 'Gestion documentaire, comparaison des banques, suivi en temps réel et co-demandeur intégré. Tout ce dont vous avez besoin pour votre hypothèque en un seul endroit.',
    },
    robots: 'index,follow',
    canonical: `/features`,
  },

  // ── Pricing ───────────────────────────────────────────────────────────────
  pricing: {
    en: {
      title: 'Pricing | HipoTech',
      description: 'Transparent plans with no surprises. Discover the cost of using HipoTech to manage your mortgage application in the Dominican Republic.',
    },
    es: {
      title: 'Precios | HipoTech',
      description: 'Planes transparentes y sin sorpresas. Conoce el costo de usar HipoTech para gestionar tu solicitud hipotecaria en República Dominicana.',
    },
    fr: {
      title: 'Tarifs | HipoTech',
      description: 'Des plans transparents sans surprises. Découvrez le coût d\'utilisation de HipoTech pour gérer votre demande de prêt hypothécaire en République Dominicaine.',
    },
    robots: 'index,follow',
    canonical: `/pricing`,
  },

  // ── Appraisers ────────────────────────────────────────────────────────────
  appraisers: {
    en: {
      title: 'Find Certified Property Appraisers | HipoTech',
      description: 'Browse certified property appraisers in the Dominican Republic. Independent valuations for mortgage applications, property purchases, and investment due diligence.',
    },
    es: {
      title: 'Tasadores Certificados de Propiedades | HipoTech',
      description: 'Encuentre tasadores certificados en República Dominicana. Valuaciones independientes para hipotecas, compras de propiedad y due diligence de inversiones.',
    },
    fr: {
      title: 'Évaluateurs Immobiliers Certifiés | HipoTech',
      description: 'Trouvez des évaluateurs certifiés en République Dominicaine. Évaluations indépendantes pour hypothèques, achats immobiliers et vérification diligente.',
    },
    robots: 'index,follow',
    canonical: '/appraisers',
  },

  // ── Book Meeting ──────────────────────────────────────────────────────────
  book: {
    en: { title: 'Book a Meeting | HipoTech', description: 'Schedule a free consultation with a HipoTech mortgage specialist to discuss your Dominican Republic property purchase.' },
    es: { title: 'Agendar Reunión | HipoTech', description: 'Agenda una consulta gratuita con un especialista hipotecario de HipoTech para discutir tu compra de propiedad en República Dominicana.' },
    fr: { title: 'Réserver un Rendez-vous | HipoTech', description: 'Planifiez une consultation gratuite avec un spécialiste hypothécaire HipoTech pour discuter de votre achat immobilier en République Dominicaine.' },
    robots: 'index,follow',
    canonical: '/book',
  },

  // ── Premium Submission ──────────────────────────────────────────────────
  premiumSubmission: {
    en: { title: 'Specialist Review | HipoTech', description: 'Submit your mortgage application for priority review by a HipoTech specialist.' },
    es: { title: 'Revisión Especializada | HipoTech', description: 'Envía tu solicitud hipotecaria para revisión prioritaria por un especialista de HipoTech.' },
    fr: { title: 'Examen Spécialisé | HipoTech', description: 'Soumettez votre demande hypothécaire pour un examen prioritaire par un spécialiste HipoTech.' },
    robots: 'noindex,nofollow',
    canonical: '/premium-submission',
  },

  // ── Fill Gateway ────────────────────────────────────────────────────────
  fill: {
    en: { title: 'Complete Your Application | HipoTech', description: 'Continue filling out your mortgage application with HipoTech.' },
    es: { title: 'Completa tu Solicitud | HipoTech', description: 'Continúa completando tu solicitud hipotecaria con HipoTech.' },
    fr: { title: 'Complétez Votre Demande | HipoTech', description: 'Continuez à remplir votre demande hypothécaire avec HipoTech.' },
    robots: 'noindex,nofollow',
    canonical: '/fill',
  },

  // ── Simulator ─────────────────────────────────────────────────────────────
  simulator: {
    en: {
      title: 'Mortgage Eligibility Simulator | HipoTech',
      description: 'Check your mortgage eligibility across Dominican Republic banks in minutes. Free, no commitment, instant results from multiple lenders.',
    },
    es: {
      title: 'Simulador de Elegibilidad Hipotecaria | HipoTech',
      description: 'Verifica tu elegibilidad hipotecaria en bancos dominicanos en minutos. Gratis, sin compromiso, resultados instantáneos de múltiples prestamistas.',
    },
    fr: {
      title: 'Simulateur d\'Éligibilité Hypothécaire | HipoTech',
      description: 'Vérifiez votre éligibilité hypothécaire auprès des banques dominicaines en quelques minutes. Gratuit, sans engagement, résultats instantanés.',
    },
    robots: 'index,follow',
    canonical: `/simulator`,
  },

  // ── Calculator ────────────────────────────────────────────────────────────
  calculator: {
    en: {
      title: 'Mortgage Calculator | HipoTech',
      description: 'Calculate your monthly mortgage payment, maximum loan amount, and see which Dominican Republic banks match your financial profile.',
    },
    es: {
      title: 'Calculadora Hipotecaria | HipoTech',
      description: 'Calcula tu pago mensual hipotecario, monto máximo de préstamo y descubre qué bancos dominicanos se ajustan a tu perfil financiero.',
    },
    fr: {
      title: 'Calculateur Hypothécaire | HipoTech',
      description: 'Calculez votre paiement hypothécaire mensuel, le montant maximal du prêt et découvrez quelles banques dominicaines correspondent à votre profil financier.',
    },
    robots: 'index,follow',
    canonical: `/calculator`,
  },

  // ── Security ──────────────────────────────────────────────────────────────
  security: {
    en: {
      title: 'Security & Privacy | HipoTech',
      description: 'Your financial information is protected with end-to-end encryption, two-factor access and compliance with international standards.',
    },
    es: {
      title: 'Seguridad y Privacidad | HipoTech',
      description: 'Tu información financiera está protegida con cifrado de extremo a extremo, acceso con doble factor y cumplimiento de normas internacionales.',
    },
    fr: {
      title: 'Sécurité et Confidentialité | HipoTech',
      description: 'Vos informations financières sont protégées par un chiffrement de bout en bout, un accès à deux facteurs et la conformité aux normes internationales.',
    },
    robots: 'index,follow',
    canonical: `/security`,
  },

  // ── Why not a broker ──────────────────────────────────────────────────────
  // Intercepts "mortgage broker Dominican Republic" searches and converts them.
  // Title intentionally contains "broker" as a keyword target.
  whyNotBroker: {
    en: {
      title: 'DR Mortgage Broker vs. HipoTech — One Application, Every Bank',
      description: 'A DR mortgage broker submits to 1–2 banks and charges a 1–2% commission. HipoTech sends your application to every qualifying DR bank simultaneously, for a flat fee — no broker, no commission, full transparency.',
    },
    es: {
      title: 'Corredor Hipotecario vs. HipoTech en RD — Una Solicitud, Todos los Bancos',
      description: 'Un corredor hipotecario en RD somete a 1–2 bancos y cobra comisión. HipoTech envía tu solicitud a todos los bancos calificados simultáneamente por una tarifa fija — sin corredor, sin comisión.',
    },
    fr: {
      title: 'Courtier Hypothécaire vs. HipoTech en RD — Une Demande, Toutes les Banques',
      description: 'Un courtier hypothécaire en RD soumet à 1–2 banques et prend une commission. HipoTech envoie votre demande à toutes les banques qualifiées simultanément, pour des frais fixes — sans courtier, sans commission.',
    },
    robots: 'index,follow',
    canonical: `/why-not-a-broker`,
    ogType: 'website',
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'DR Mortgage Broker vs. HipoTech',
      description: 'Comparison of traditional DR mortgage brokers versus the HipoTech multi-bank application platform.',
      url: `${BASE_URL}/why-not-a-broker`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home',             item: `${BASE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Why Not a Broker', item: `${BASE_URL}/why-not-a-broker` },
        ],
      },
    },
  },

  // ── About ─────────────────────────────────────────────────────────────────
  about: {
    en: {
      title: 'About Us | HipoTech',
      description: 'We are the team modernizing the Dominican mortgage market. Discover our mission, values and the story behind HipoTech.',
    },
    es: {
      title: 'Sobre Nosotros | HipoTech',
      description: 'Somos el equipo que está modernizando el mercado hipotecario dominicano. Conoce nuestra misión, valores y la historia detrás de HipoTech.',
    },
    fr: {
      title: 'À Propos de Nous | HipoTech',
      description: 'Nous sommes l\'équipe qui modernise le marché hypothécaire dominicain. Découvrez notre mission, nos valeurs et l\'histoire derrière HipoTech.',
    },
    robots: 'index,follow',
    canonical: `/about`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'HipoTech',
      url: BASE_URL,
      logo: `${BASE_URL}/media/logo/hipotech_black_horizontal.png`,
      description: 'HipoTech is a multi-bank mortgage platform for buyers in the Dominican Republic. Submit one application — every qualifying bank receives it simultaneously.',
      foundingLocation: {
        '@type': 'Place',
        name: 'Dominican Republic',
      },
      areaServed: {
        '@type': 'Country',
        name: 'Dominican Republic',
      },
      serviceType: 'Mortgage Brokerage Platform',
      availableLanguage: ['English', 'Spanish', 'French'],
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        availableLanguage: ['English', 'Spanish', 'French'],
        url: `${BASE_URL}/contact`,
      },
      sameAs: [
        'https://www.facebook.com/hipotech',
        'https://www.instagram.com/hipotech',
      ],
    },
  },

  // ── Contact ───────────────────────────────────────────────────────────────
  contact: {
    en: {
      title: 'Contact Us | HipoTech',
      description: 'Write to us with your questions, suggestions or support requests. We respond within 24 hours.',
    },
    es: {
      title: 'Contáctanos | HipoTech',
      description: 'Escríbenos con tus preguntas, sugerencias o solicitudes de soporte. Respondemos en menos de 24 horas.',
    },
    fr: {
      title: 'Contactez-Nous | HipoTech',
      description: 'Écrivez-nous avec vos questions, suggestions ou demandes d\'assistance. Nous répondons dans les 24 heures.',
    },
    robots: 'index,follow',
    canonical: `/contact`,
  },

  // ── Blog index ────────────────────────────────────────────────────────────
  blog: {
    en: {
      title: 'Blog: DR Mortgages & Real Estate Insights | HipoTech',
      description: 'Articles on interest rates, the Dominican real estate market, investment strategies and tips for first-time buyers.',
    },
    es: {
      title: 'Blog sobre Hipotecas y Bienes Raíces en RD | HipoTech',
      description: 'Artículos sobre tasas de interés, mercado inmobiliario dominicano, estrategias de inversión y consejos para compradores de primera vivienda.',
    },
    fr: {
      title: 'Blog : Hypothèques et Immobilier en RD | HipoTech',
      description: 'Articles sur les taux d\'intérêt, le marché immobilier dominicain, les stratégies d\'investissement et les conseils pour les primo-accédants.',
    },
    robots: 'index,follow',
    canonical: `/blog`,
    ogType: 'website',
  },

  // ── Blog posts ────────────────────────────────────────────────────────────
  blogInterestRates: {
    en: {
      title: 'Why Are Interest Rates So High in the Dominican Republic?',
      description: 'Understanding nominal rates, currency depreciation, and what it really costs to borrow in DOP vs USD — the full picture behind DR mortgage rates.',
    },
    es: {
      title: 'Tasas de Interés Hipotecario en República Dominicana',
      description: 'Entiende la diferencia entre tasa nominal y real, el impacto de la depreciación del peso y cómo comparar hipotecas en DOP vs USD.',
    },
    fr: {
      title: 'Pourquoi les Taux d\'Intérêt sont-ils si Élevés en République Dominicaine?',
      description: 'Comprendre les taux nominaux, la dépréciation de la monnaie et le coût réel d\'emprunt en DOP vs USD — le tableau complet des taux hypothécaires en RD.',
    },
    robots: 'index,follow',
    canonical: `/blog/interest-rates-dr`,
    ogType: 'article',
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Why Are Interest Rates So High in the Dominican Republic?',
      description: 'Understanding nominal rates, currency depreciation, and what it really costs to borrow in DOP vs USD — the full picture behind DR mortgage rates.',
      image: `${BASE_URL}/og-image.png`,
      datePublished: '2025-10-01',
      dateModified: '2025-10-01',
      author: { '@type': 'Organization', name: 'HipoTech', url: BASE_URL },
      publisher: { '@type': 'Organization', name: 'HipoTech', logo: { '@type': 'ImageObject', url: `${BASE_URL}/media/logo/hipotech_black_horizontal.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}/blog/interest-rates-dr` },
    },
  },

  blogDelaysInDR: {
    en: {
      title: 'Understanding Delays in the Dominican Republic Real Estate Market',
      description: 'The main causes of delays in Dominican real estate transactions and how to avoid them with the right preparation and documentation.',
    },
    es: {
      title: 'Por Qué se Retrasan los Cierres Inmobiliarios en RD',
      description: 'Las principales causas de demora en transacciones de bienes raíces dominicanas y cómo evitarlas con preparación y la documentación correcta.',
    },
    fr: {
      title: 'Comprendre les Retards dans l\'Immobilier en République Dominicaine',
      description: 'Les principales causes de retard dans les transactions immobilières dominicaines et comment les éviter avec la bonne préparation.',
    },
    robots: 'index,follow',
    canonical: `/blog/delays-in-dr`,
    ogType: 'article',
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Understanding Delays in the Dominican Republic Real Estate Market',
      description: 'The main causes of delays in Dominican real estate transactions and how to avoid them with the right preparation and documentation.',
      image: `${BASE_URL}/og-image.png`,
      datePublished: '2025-10-03',
      dateModified: '2025-10-03',
      author: { '@type': 'Organization', name: 'HipoTech', url: BASE_URL },
      publisher: { '@type': 'Organization', name: 'HipoTech', logo: { '@type': 'ImageObject', url: `${BASE_URL}/media/logo/hipotech_black_horizontal.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}/blog/delays-in-dr` },
    },
  },

  blogPreApproveMortgage: {
    en: {
      title: 'How to Get Mortgage Pre-Approval in the Dominican Republic (DR)',
      description: 'Steps to get your DR mortgage pre-approval letter, required documents for foreign buyers, and why pre-approval is essential before making a purchase offer in the Dominican Republic.',
    },
    es: {
      title: 'Cómo Obtener Pre-Aprobación Hipotecaria en República Dominicana',
      description: 'Pasos para conseguir tu carta de pre-aprobación, qué documentos necesitas y por qué es clave antes de hacer una oferta de compra.',
    },
    fr: {
      title: 'Comment Obtenir une Pré-Approbation Hypothécaire en République Dominicaine',
      description: 'Les étapes pour obtenir votre lettre de pré-approbation, les documents nécessaires et pourquoi c\'est essentiel avant de faire une offre d\'achat.',
    },
    robots: 'index,follow',
    canonical: `/blog/pre-approve-mortgage`,
    ogType: 'article',
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'How to Get Mortgage Pre-Approval in the Dominican Republic',
      description: 'Steps to get your DR mortgage pre-approval letter, required documents for foreign buyers, and why pre-approval is essential before making a purchase offer.',
      image: `${BASE_URL}/og-image.png`,
      datePublished: '2025-10-04',
      dateModified: '2025-10-04',
      author: { '@type': 'Organization', name: 'HipoTech', url: BASE_URL },
      publisher: { '@type': 'Organization', name: 'HipoTech', logo: { '@type': 'ImageObject', url: `${BASE_URL}/media/logo/hipotech_black_horizontal.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}/blog/pre-approve-mortgage` },
    },
  },

  blogPropertyAppreciation: {
    en: {
      title: 'Property Appreciation in the Dominican Republic',
      description: 'Analysis of historical trends and projections for the Dominican real estate market: which areas have appreciated most and why.',
    },
    es: {
      title: 'Apreciación de Propiedades en la República Dominicana',
      description: 'Análisis de tendencias históricas y proyecciones del mercado inmobiliario dominicano: qué zonas han valorizado más y por qué.',
    },
    fr: {
      title: 'Valorisation Immobilière en République Dominicaine',
      description: 'Analyse des tendances historiques et projections du marché immobilier dominicain : quelles zones ont le plus apprécié et pourquoi.',
    },
    robots: 'index,follow',
    canonical: `/blog/property-appreciation`,
    ogType: 'article',
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Property Appreciation in the Dominican Republic',
      description: 'Analysis of historical trends and projections for the Dominican real estate market: which areas have appreciated most and why.',
      image: `${BASE_URL}/og-image.png`,
      datePublished: '2025-10-07',
      dateModified: '2025-10-07',
      author: { '@type': 'Organization', name: 'HipoTech', url: BASE_URL },
      publisher: { '@type': 'Organization', name: 'HipoTech', logo: { '@type': 'ImageObject', url: `${BASE_URL}/media/logo/hipotech_black_horizontal.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}/blog/property-appreciation` },
    },
  },

  blogBankCapital: {
    en: {
      title: 'Do Dominican Banks Have the Capital to Lend? | HipoTech',
      description: 'At 5.5% of GDP, the DR mortgage market is practically untouched. IMF data shows banks are well-capitalized and ready to lend.',
    },
    es: {
      title: 'Capital Bancario y su Impacto en tu Hipoteca en RD',
      description: 'Cómo los requerimientos de capital de los bancos dominicanos afectan la disponibilidad y condiciones de los préstamos hipotecarios.',
    },
    fr: {
      title: 'Les Banques Dominicaines ont-elles le Capital pour Prêter?',
      description: 'À 5,5% du PIB, le marché hypothécaire dominicain est pratiquement inexploité. Les données du FMI montrent que les banques sont bien capitalisées.',
    },
    robots: 'index,follow',
    canonical: `/blog/bank-capital`,
    ogType: 'article',
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Do Dominican Banks Have the Capital to Lend?',
      description: 'At 5.5% of GDP, the DR mortgage market is practically untouched. IMF data shows banks are well-capitalized and ready to lend.',
      image: `${BASE_URL}/og-image.png`,
      datePublished: '2025-10-19',
      dateModified: '2025-10-19',
      author: { '@type': 'Organization', name: 'HipoTech', url: BASE_URL },
      publisher: { '@type': 'Organization', name: 'HipoTech', logo: { '@type': 'ImageObject', url: `${BASE_URL}/media/logo/hipotech_black_horizontal.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}/blog/bank-capital` },
    },
  },

  blogDailyRentals: {
    en: {
      title: 'Buy Property in DR, Rent It Daily: STR & CONFOTUR Investor Guide',
      description: '11.6 million visitors, no national short-term rental restrictions, and CONFOTUR tax incentives. How to finance an STR investment property in the Dominican Republic with a mortgage.',
    },
    es: {
      title: 'Alquiler Diario vs. Largo Plazo en RD: ¿Cuál Conviene?',
      description: 'Comparamos la rentabilidad, riesgo y gestión de ambas estrategias de inversión inmobiliaria en el mercado turístico dominicano.',
    },
    fr: {
      title: 'Acheter une Propriété, la Louer au Quotidien : Guide STR en RD',
      description: '11,6 millions de visiteurs, aucune restriction nationale de location à court terme et incitations fiscales CONFOTUR. Tout ce qu\'un acheteur étranger doit savoir.',
    },
    robots: 'index,follow',
    canonical: `/blog/daily-rentals`,
    ogType: 'article',
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Buy Property in DR, Rent It Daily: STR & CONFOTUR Investor Guide',
      description: '11.6 million visitors, no national short-term rental restrictions, and CONFOTUR tax incentives. How to finance an STR investment property in the Dominican Republic.',
      image: `${BASE_URL}/og-image.png`,
      datePublished: '2025-10-21',
      dateModified: '2025-10-21',
      author: { '@type': 'Organization', name: 'HipoTech', url: BASE_URL },
      publisher: { '@type': 'Organization', name: 'HipoTech', logo: { '@type': 'ImageObject', url: `${BASE_URL}/media/logo/hipotech_black_horizontal.png` } },
      mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}/blog/daily-rentals` },
    },
  },

  // ── Legal ─────────────────────────────────────────────────────────────────
  privacyPolicy: {
    en: { title: 'Privacy Policy | HipoTech', description: 'How HipoTech collects, uses and protects your personal information.' },
    es: { title: 'Política de Privacidad | HipoTech', description: 'Cómo HipoTech recopila, usa y protege tu información personal.' },
    fr: { title: 'Politique de Confidentialité | HipoTech', description: 'Comment HipoTech collecte, utilise et protège vos informations personnelles.' },
    robots: 'index,follow',
    canonical: `/privacy-policy`,
  },

  termsOfService: {
    en: { title: 'Terms of Service | HipoTech', description: 'Terms and conditions for using the HipoTech platform.' },
    es: { title: 'Términos de Servicio | HipoTech', description: 'Condiciones de uso de la plataforma HipoTech.' },
    fr: { title: 'Conditions d\'Utilisation | HipoTech', description: 'Conditions d\'utilisation de la plateforme HipoTech.' },
    robots: 'index,follow',
    canonical: `/terms-of-service`,
  },

  compliance: {
    en: { title: 'Regulatory Compliance | HipoTech', description: 'HipoTech\'s regulatory framework and compliance measures.' },
    es: { title: 'Cumplimiento Normativo | HipoTech', description: 'Marco regulatorio y medidas de cumplimiento de HipoTech.' },
    fr: { title: 'Conformité Réglementaire | HipoTech', description: 'Cadre réglementaire et mesures de conformité de HipoTech.' },
    robots: 'index,follow',
    canonical: `/compliance`,
  },

  securityPolicy: {
    en: { title: 'Security Policy | HipoTech', description: 'HipoTech\'s information security standards and practices.' },
    es: { title: 'Política de Seguridad | HipoTech', description: 'Estándares y prácticas de seguridad de la información en HipoTech.' },
    fr: { title: 'Politique de Sécurité | HipoTech', description: 'Normes et pratiques de sécurité de l\'information chez HipoTech.' },
    robots: 'index,follow',
    canonical: `/security-policy`,
  },

  // ── Diligence portal ──────────────────────────────────────────────────────
  diligenceOverview: {
    en: { title: 'Due Diligence for Banks & Regulators | HipoTech', description: 'Technical, financial and regulatory documentation of HipoTech for entities evaluating the platform.' },
    es: { title: 'Due Diligence para Bancos y Reguladores | HipoTech', description: 'Documentación técnica, financiera y regulatoria de HipoTech para entidades que evalúan la plataforma.' },
    fr: { title: 'Diligence Raisonnable pour Banques et Régulateurs | HipoTech', description: 'Documentation technique, financière et réglementaire de HipoTech pour les entités évaluant la plateforme.' },
    robots: 'index,follow',
    canonical: `/diligence/overview`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Due Diligence', item: `${BASE_URL}/diligence/overview` },
      ],
    },
  },

  diligenceBuyers: {
    en: { title: 'For Buyers | HipoTech Diligence', description: 'Verification process, data protection and commitments to buyers using HipoTech.' },
    es: { title: 'Para Compradores | HipoTech Diligence', description: 'Proceso de verificación, protección de datos y compromisos con los compradores que usan HipoTech.' },
    fr: { title: 'Pour les Acheteurs | HipoTech Diligence', description: 'Processus de vérification, protection des données et engagements envers les acheteurs utilisant HipoTech.' },
    robots: 'index,follow',
    canonical: `/diligence/buyers`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Due Diligence', item: `${BASE_URL}/diligence/overview` },
        { '@type': 'ListItem', position: 3, name: 'For Buyers', item: `${BASE_URL}/diligence/buyers` },
      ],
    },
  },

  diligenceSellers: {
    en: { title: 'For Sellers | HipoTech Diligence', description: 'How HipoTech protects the interests of sellers in mortgage transactions.' },
    es: { title: 'Para Vendedores | HipoTech Diligence', description: 'Cómo HipoTech protege los intereses de vendedores en transacciones hipotecarias.' },
    fr: { title: 'Pour les Vendeurs | HipoTech Diligence', description: 'Comment HipoTech protège les intérêts des vendeurs dans les transactions hypothécaires.' },
    robots: 'index,follow',
    canonical: `/diligence/sellers`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Due Diligence', item: `${BASE_URL}/diligence/overview` },
        { '@type': 'ListItem', position: 3, name: 'For Sellers', item: `${BASE_URL}/diligence/sellers` },
      ],
    },
  },

  diligenceBanks: {
    en: { title: 'For Banks | HipoTech Diligence', description: 'Bank integration, data security and risk assessment processes at HipoTech.' },
    es: { title: 'Para Bancos | HipoTech Diligence', description: 'Integración bancaria, seguridad de datos y procesos de evaluación de riesgo en HipoTech.' },
    fr: { title: 'Pour les Banques | HipoTech Diligence', description: 'Intégration bancaire, sécurité des données et processus d\'évaluation des risques chez HipoTech.' },
    robots: 'index,follow',
    canonical: `/diligence/banks`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Due Diligence', item: `${BASE_URL}/diligence/overview` },
        { '@type': 'ListItem', position: 3, name: 'For Banks', item: `${BASE_URL}/diligence/banks` },
      ],
    },
  },

  diligenceAgents: {
    en: { title: 'For Real Estate Agents | HipoTech Diligence', description: 'Agent role, platform access and responsibilities within the HipoTech ecosystem.' },
    es: { title: 'Para Agentes Inmobiliarios | HipoTech Diligence', description: 'Rol del agente, acceso a la plataforma y responsabilidades dentro del ecosistema HipoTech.' },
    fr: { title: 'Pour les Agents Immobiliers | HipoTech Diligence', description: 'Rôle de l\'agent, accès à la plateforme et responsabilités au sein de l\'écosystème HipoTech.' },
    robots: 'index,follow',
    canonical: `/diligence/agents`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Due Diligence', item: `${BASE_URL}/diligence/overview` },
        { '@type': 'ListItem', position: 3, name: 'For Real Estate Agents', item: `${BASE_URL}/diligence/agents` },
      ],
    },
  },

  diligenceRegulators: {
    en: { title: 'For Regulators | HipoTech Diligence', description: 'Regulatory compliance framework, AML/KYC controls and reports available for supervisory bodies.' },
    es: { title: 'Para Reguladores | HipoTech Diligence', description: 'Marco de cumplimiento regulatorio, controles AML/KYC y reportes disponibles para entes supervisores.' },
    fr: { title: 'Pour les Régulateurs | HipoTech Diligence', description: 'Cadre de conformité réglementaire, contrôles AML/KYC et rapports disponibles pour les organismes de supervision.' },
    robots: 'index,follow',
    canonical: `/diligence/regulators`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Due Diligence', item: `${BASE_URL}/diligence/overview` },
        { '@type': 'ListItem', position: 3, name: 'For Regulators', item: `${BASE_URL}/diligence/regulators` },
      ],
    },
  },

  diligenceAppraisers: {
    en: { title: 'For Appraisers | HipoTech Diligence', description: 'Integration of the appraisal process within HipoTech\'s mortgage workflow.' },
    es: { title: 'Para Tasadores | HipoTech Diligence', description: 'Integración del proceso de tasación dentro del flujo hipotecario de HipoTech.' },
    fr: { title: 'Pour les Évaluateurs | HipoTech Diligence', description: 'Intégration du processus d\'évaluation dans le flux hypothécaire de HipoTech.' },
    robots: 'index,follow',
    canonical: `/diligence/appraisers`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Due Diligence', item: `${BASE_URL}/diligence/overview` },
        { '@type': 'ListItem', position: 3, name: 'For Appraisers', item: `${BASE_URL}/diligence/appraisers` },
      ],
    },
  },

  diligenceIT: {
    en: { title: 'IT Security | HipoTech Diligence', description: 'Security architecture, encryption, access controls and business continuity plan at HipoTech.' },
    es: { title: 'Seguridad de TI | HipoTech Diligence', description: 'Arquitectura de seguridad, cifrado, controles de acceso y plan de continuidad de negocio de HipoTech.' },
    fr: { title: 'Sécurité Informatique | HipoTech Diligence', description: 'Architecture de sécurité, chiffrement, contrôles d\'accès et plan de continuité des activités chez HipoTech.' },
    robots: 'index,follow',
    canonical: `/diligence/it`,
    schemaOrg: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: BASE_URL },
        { '@type': 'ListItem', position: 2, name: 'Due Diligence', item: `${BASE_URL}/diligence/overview` },
        { '@type': 'ListItem', position: 3, name: 'IT Security', item: `${BASE_URL}/diligence/it` },
      ],
    },
  },

  // ── Auth / private / admin (noindex — title only needed) ─────────────────
  login:            { en: { title: 'Sign In | HipoTech' }, es: { title: 'Iniciar Sesión | HipoTech' }, fr: { title: 'Se Connecter | HipoTech' }, robots: 'noindex,nofollow' },
  register:         { en: { title: 'Create Account | HipoTech' }, es: { title: 'Crear Cuenta | HipoTech' }, fr: { title: 'Créer un Compte | HipoTech' }, robots: 'noindex,nofollow' },
  forgotPassword:   { en: { title: 'Reset Password | HipoTech' }, es: { title: 'Recuperar Contraseña | HipoTech' }, fr: { title: 'Réinitialiser le Mot de Passe | HipoTech' }, robots: 'noindex,nofollow' },
  verifyEmail:      { en: { title: 'Verify Email | HipoTech' }, es: { title: 'Verificar Correo | HipoTech' }, fr: { title: 'Vérifier l\'Email | HipoTech' }, robots: 'noindex,nofollow' },
  dashboard:        { en: { title: 'My Dashboard | HipoTech' }, es: { title: 'Mi Dashboard | HipoTech' }, fr: { title: 'Mon Tableau de Bord | HipoTech' }, robots: 'noindex,nofollow' },
  documents:        { en: { title: 'My Documents | HipoTech' }, es: { title: 'Mis Documentos | HipoTech' }, fr: { title: 'Mes Documents | HipoTech' }, robots: 'noindex,nofollow' },
  profile:          { en: { title: 'My Profile | HipoTech' }, es: { title: 'Mi Perfil | HipoTech' }, fr: { title: 'Mon Profil | HipoTech' }, robots: 'noindex,nofollow' },
  applicationFlow:  { en: { title: 'My Application | HipoTech' }, es: { title: 'Mi Solicitud | HipoTech' }, fr: { title: 'Ma Demande | HipoTech' }, robots: 'noindex,nofollow' },
  notFound:         { en: { title: 'Page Not Found | HipoTech' }, es: { title: 'Página no encontrada | HipoTech' }, fr: { title: 'Page Introuvable | HipoTech' }, robots: 'noindex,nofollow' },
  admin:            { en: { title: 'Admin | HipoTech' }, es: { title: 'Admin | HipoTech' }, fr: { title: 'Admin | HipoTech' }, robots: 'noindex,nofollow' },

  // ── Pre-construction blog SEO (from seoConfig.blogs.ts) ──
  ...BATCH6_PRECON_BLOG_SEO,
};

// ── Public API ──────────────────────────────────────────────────────────────

/**
 * Returns the SEO props for a given page and language.
 * Falls back to English if the language isn't found.
 */
/** Blog titles for SEO — used when blog pages aren't individually registered in SEO_REGISTRY */
const BLOG_SEO_TITLES: Record<string, string> = {
  blogForeignerMortgage: 'How to Get a Mortgage in the Dominican Republic as a Foreigner',
  blogDocumentsDrMortgage: 'What Documents Do I Need to Apply for a Mortgage in the Dominican Republic?',
  blogDownPaymentDr: 'What Is the Minimum Down Payment for a Mortgage in the Dominican Republic?',
  blogUSCitizenMortgage: 'Can a US Citizen Get a Mortgage in the Dominican Republic?',
  blogCreditScoreDr: 'What Credit Score Do Dominican Banks Require for a Mortgage?',
  blogNonResidentMortgage: 'Can a Non-Resident Buy Property in the Dominican Republic with a Mortgage?',
  blogApprovalTimeDr: 'How Long Does Mortgage Approval Take in the Dominican Republic?',
  blogDrVsUsMortgage: 'How Does the Dominican Republic Mortgage Process Differ from the US?',
  blogMortgageFeesDr: 'What Fees Should I Expect When Getting a Mortgage in the Dominican Republic?',
  blogAfterApprovalDr: 'What Happens After My Mortgage Is Approved in the Dominican Republic?',
  blogPopularVsBanreservas: 'Banco Popular vs. Banreservas: Which Is Better for a Mortgage in the DR?',
  blogApapVsBhd: 'APAP vs. BHD Mortgage Rates: What You Need to Know',
  blogLowestMortgageRate: 'Which Dominican Bank Has the Lowest Mortgage Rate Right Now?',
  blogFixedVsVariable: 'Fixed vs. Variable Rate Mortgages in the Dominican Republic',
  blogEligibilityComparison: 'How Dominican Bank Eligibility Criteria Compare — A Side-by-Side Breakdown',
  blogDrVsUsaMortgage: 'Dominican Republic vs. United States: How the Mortgage Process Compares',
  blogDrVsCanadaMortgage: 'Dominican Republic vs. Canada: How the Mortgage Process Compares',
  blogBackTaxesCashProperty: 'Can I Be Liable for Back Taxes on a Property I Just Bought?',
  blogTasacionDr: 'What Is a Tasación in the Dominican Republic — and Why Does It Matter for Your Mortgage?',
  blogFindAppraiserDr: 'How to Find a Certified Appraiser (Tasador) in the Dominican Republic',
  blogAppraisalCostDr: 'How Much Does a Property Appraisal (Tasación) Cost in the Dominican Republic?',
  blogBankAppraisalRequirements: 'What Do Dominican Banks Look for in a Property Appraisal?',
  blogAppraiserRejected: 'What Happens If the Bank Rejects My Appraiser in the Dominican Republic?',
  blogPuntaCanaBuyersGuide: "Punta Cana Property Buyer's Guide",
  blogDominicansAbroadMortgage: 'Can Dominicans Living Abroad Get a Mortgage in the Dominican Republic?',
  blogDiasporaBuyerGuide: "The Diaspora Buyer's Guide to DR Real Estate",
  blogUsIncomeDrMortgage: 'How to Qualify for a Dominican Republic Mortgage with US Income',
  blogApplyRemotely: 'Can I Apply for a Dominican Republic Mortgage Without Being in the Country?',
  blogConfoturGuide: 'What Is CONFOTUR? The Complete Guide for Foreign Property Buyers in the Dominican Republic',
  blogConfoturTransferTax: 'CONFOTUR Transfer Tax Exemption: How to Save 3% on Your Dominican Republic Property Purchase',
  blogConfoturRentalIncome: 'CONFOTUR Rental Income Tax Benefits: How to Legally Reduce Your Dominican Republic Rental Tax to Zero',
  blogFindConfoturProperties: 'How to Find CONFOTUR-Certified Properties in the Dominican Republic',
  blogConfoturMortgageBenefits: 'How CONFOTUR Certification Affects Your Mortgage and Total Cost of Acquiring Property in the DR',
  blogConfoturZones: 'Where Are the CONFOTUR Zones? A Map of Tax-Incentivized Real Estate in the DR',
  blogDrMarketOutlook: 'Dominican Republic Property Market Outlook 2026',
  blogBestAreasInvest: 'Best Areas to Invest in Dominican Republic Real Estate in 2026',
  blogDrVsCaribbean: 'Dominican Republic vs. Other Caribbean Real Estate Markets: An Honest Comparison',
  blogDrRentalYieldAnalysis: 'How to Analyze Rental Yields in the Dominican Republic',
  blogDrTorrensTitle: 'The Dominican Republic Title System Explained: The Torrens System for Foreign Buyers',
  blogCoApplicant: 'Adding a Co-Applicant to Your Dominican Republic Mortgage',
  blogSelfEmployedMortgage: 'Getting a DR Mortgage with Self-Employed Income: What Banks Actually Want',
  blogPreConstructionMortgage: 'Buying Pre-Construction Property in the Dominican Republic: How Financing Works',
  blogRetirementProperty: 'Getting a DR Mortgage for Retirement Property: What Retirees Need to Know',
  blogInterestRates: 'Why Are Interest Rates So High in the Dominican Republic?',
  blogDelaysInDR: 'Understanding Delays in the Dominican Republic',
  blogPreApproveMortgage: 'Why Pre-Approve a Mortgage on Your Property for Sale?',
  blogPropertyAppreciation: 'Yes, Rates Are High. But So Are Returns.',
  blogBankCapital: 'Do Banks Have the Capital to Mortgage in the Dominican Republic?',
  blogDailyRentals: "Buy a Property, Rent It Daily: The Investor's Guide to Short-Term Rentals in the DR",
};

/** Convert blog page key to URL slug: blogForeignerMortgage → foreigner-mortgage-dr */
const BLOG_KEY_TO_SLUG: Record<string, string> = {
  blogForeignerMortgage: 'foreigner-mortgage-dr',
  blogDocumentsDrMortgage: 'documents-dr-mortgage',
  blogDownPaymentDr: 'down-payment-dr-mortgage',
  blogUSCitizenMortgage: 'us-citizen-mortgage-dr',
  blogCreditScoreDr: 'credit-score-dr-mortgage',
  blogNonResidentMortgage: 'non-resident-mortgage-dr',
  blogApprovalTimeDr: 'mortgage-approval-time-dr',
  blogDrVsUsMortgage: 'dr-vs-us-mortgage',
  blogMortgageFeesDr: 'mortgage-fees-dr',
  blogAfterApprovalDr: 'after-mortgage-approval-dr',
  blogPopularVsBanreservas: 'banco-popular-vs-banreservas-mortgage',
  blogApapVsBhd: 'apap-vs-bhd-mortgage',
  blogLowestMortgageRate: 'lowest-mortgage-rate-dr',
  blogFixedVsVariable: 'fixed-vs-variable-rate-dr',
  blogEligibilityComparison: 'dr-bank-eligibility-comparison',
  blogDrVsUsaMortgage: 'dr-vs-usa-mortgage',
  blogDrVsCanadaMortgage: 'dr-vs-canada-mortgage',
  blogBackTaxesCashProperty: 'back-taxes-cash-property-dr',
  blogTasacionDr: 'tasacion-dr-mortgage',
  blogFindAppraiserDr: 'find-certified-appraiser-dr',
  blogAppraisalCostDr: 'property-appraisal-cost-dr',
  blogBankAppraisalRequirements: 'what-banks-look-for-appraisal-dr',
  blogAppraiserRejected: 'bank-rejected-appraiser-dr',
  blogPuntaCanaBuyersGuide: 'punta-cana-buyers-guide',
  blogDominicansAbroadMortgage: 'dominicans-abroad-mortgage',
  blogDiasporaBuyerGuide: 'diaspora-buyer-guide-dr',
  blogUsIncomeDrMortgage: 'us-income-dr-mortgage',
  blogApplyRemotely: 'apply-dr-mortgage-remotely',
  blogConfoturGuide: 'confotur-guide',
  blogConfoturTransferTax: 'confotur-transfer-tax-exemption',
  blogConfoturRentalIncome: 'confotur-rental-income-tax',
  blogFindConfoturProperties: 'find-confotur-properties',
  blogConfoturMortgageBenefits: 'confotur-mortgage-benefits',
  blogConfoturZones: 'confotur-zones',
  blogDrMarketOutlook: 'dr-property-market-outlook',
  blogBestAreasInvest: 'best-areas-invest-dr',
  blogDrVsCaribbean: 'dr-vs-caribbean-real-estate',
  blogDrRentalYieldAnalysis: 'dr-rental-yield-analysis',
  blogDrTorrensTitle: 'dr-torrens-title-system',
  blogCoApplicant: 'co-applicant-dr-mortgage',
  blogSelfEmployedMortgage: 'self-employed-dr-mortgage',
  blogPreConstructionMortgage: 'pre-construction-dr-mortgage',
  blogRetirementProperty: 'retirement-property-dr-mortgage',
  blogInterestRates: 'interest-rates-dr',
  blogDelaysInDR: 'delays-in-dr',
  blogPreApproveMortgage: 'pre-approve-mortgage',
  blogPropertyAppreciation: 'property-appreciation',
  blogBankCapital: 'bank-capital',
  blogDailyRentals: 'daily-rentals',
};

export function getPageSEO(pageKey: string, language: Language): SEOProps {
  const entry = SEO_REGISTRY[pageKey];
  if (!entry) {
    // Check blog title map before falling back to generic
    const blogTitle = BLOG_SEO_TITLES[pageKey];
    if (blogTitle) {
      const slug = BLOG_KEY_TO_SLUG[pageKey];
      const canonical = slug ? `/blog/${slug}` : undefined;
      const fullUrl = slug ? `https://hipotech.net/blog/${slug}` : undefined;

      return {
        title: blogTitle,
        robots: 'index,follow',
        canonical,
        ogType: 'article',
        schemaOrg: {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: blogTitle,
          author: {
            '@type': 'Organization',
            name: 'HipoTech',
            url: 'https://hipotech.net',
          },
          publisher: {
            '@type': 'Organization',
            name: 'HipoTech',
            url: 'https://hipotech.net',
            logo: {
              '@type': 'ImageObject',
              url: 'https://hipotech.net/logo192.png',
            },
          },
          ...(fullUrl ? { mainEntityOfPage: fullUrl } : {}),
          inLanguage: language === 'es' ? 'es' : language === 'fr' ? 'fr' : 'en',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hipotech.net' },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://hipotech.net/blog' },
              { '@type': 'ListItem', position: 3, name: blogTitle, ...(fullUrl ? { item: fullUrl } : {}) },
            ],
          },
        },
      };
    }
    console.warn(`[seoConfig] No SEO entry found for page key: "${pageKey}"`);
    return { title: 'HipoTech', robots: 'index,follow' };
  }

  const localized = entry[language] ?? entry.en;

  return {
    title: localized.title,
    description: localized.description,
    robots: entry.robots ?? 'index,follow',
    canonical: entry.canonical,
    ogType: entry.ogType,
    schemaOrg: entry.schemaOrg,
  };
}

/**
 * React hook — reads the active language automatically.
 *
 * Usage: usePageSEO('home')
 */
export function usePageSEO(pageKey: string, overrides?: Partial<SEOProps>) {
  const { language } = useLanguage();
  const seo = getPageSEO(pageKey, language);
  useSEO({ ...seo, ...overrides });
}